import React from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/Input';
import Switch from '../../../../components/Switch';
import Dropdown from '../../../../components/Dropdown';
import TextArea from '../../../../components/TextArea';
import ImageUpload from '../../../../components/ImageUpload';
import PhoneInput from '../../../../components/PhoneInput';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { Validation } from '../../../../interfaces/common';
import { AddEditApi } from '../../../../interfaces/developer.interface';

interface DeveloperDetailsProps {
  edit: boolean;
  view?: boolean;
  formData: AddEditApi;
  validation: Validation;
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any, name?: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
  countries?: any[];
  states?: any[];
  cities?: any[];
  areas?: any[];
  onRemoveLogo?: () => void;
}

const DeveloperDetails: React.FC<DeveloperDetailsProps> = ({
  view,
  formData,
  validation,
  dropdownSearchData,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange,
  cities = [],
  states = [],
  countries = [],
  areas = [],
  onRemoveLogo,
}) => {
  const { t } = useTranslation();

  return (
    <form className="card-body" autoComplete="off">
      {/* Section 1: Primary Details */}
      <div className="row g-6">
        <h6 className="mb-0">1. {t('developers.addEdit.pageTexts.form.sections.primaryDetails')}</h6>

        {/* Logo - col-12 */}
        <div className="col-12">
          <ImageUpload
            id={VALIDATION_FIELD_MAPPING.logoFile}
            name={VALIDATION_FIELD_MAPPING.logoFile}
            disabled={view}
            accept="image/png, image/jpeg, image/gif"
            error={
              !!validation.fields.find(
                (field) => field.field === VALIDATION_FIELD_MAPPING.logoFile
              )
            }
            errorMessage={
              validation.fields.find(
                (field) => field.field === VALIDATION_FIELD_MAPPING.logoFile
              )?.message ?? ''
            }
            label={t('developers.addEdit.pageTexts.form.logo.label')}
            value={formData.logoFile}
            previewUrl={formData.logo}
            onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.logoFile)}
            onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.logoFile)}
            enableCrop={true}
            onRemove={onRemoveLogo}
          />
        </div>

        {/* Name - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.name}
              name={VALIDATION_FIELD_MAPPING.name}
              disabled={view}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.name
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.name
                )?.message ?? ''
              }
              label={t('developers.addEdit.pageTexts.form.name.label')}
              placeholder={t('developers.addEdit.pageTexts.form.name.placeholder')}
              value={formData.name}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.name)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.name)}
            />
          </div>
        </div>

        {/* Alias Name - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.alias_name}
              name={VALIDATION_FIELD_MAPPING.alias_name}
              disabled={view}
              label={t('developers.addEdit.pageTexts.form.aliasName.label')}
              placeholder={t('developers.addEdit.pageTexts.form.aliasName.placeholder')}
              value={formData.alias_name}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.alias_name)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.alias_name)}
            />
          </div>
        </div>

        {/* Code - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.code}
              name={VALIDATION_FIELD_MAPPING.code}
              disabled={view}
              label={t('developers.addEdit.pageTexts.form.code.label')}
              placeholder={t('developers.addEdit.pageTexts.form.code.placeholder')}
              value={formData.code}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.code)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.code)}
            />
          </div>
        </div>

        {/* Primary Owner Name - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.primary_owner_name}
              name={VALIDATION_FIELD_MAPPING.primary_owner_name}
              disabled={view}
              label={t('developers.addEdit.pageTexts.form.primaryOwnerName.label')}
              placeholder={t('developers.addEdit.pageTexts.form.primaryOwnerName.placeholder')}
              value={formData.primary_owner_name}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.primary_owner_name)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.primary_owner_name)}
            />
          </div>
        </div>

        {/* Email - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.email}
              name={VALIDATION_FIELD_MAPPING.email}
              disabled={view}
              label={t('developers.addEdit.pageTexts.form.email.label')}
              placeholder={t('developers.addEdit.pageTexts.form.email.placeholder')}
              value={formData.email}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.email)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.email)}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.email
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.email
                )?.message ?? ''
              }
            />
          </div>
        </div>

        {/* Phone - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <PhoneInput
              type="text"
              id={'phone'}
              name={'phone'}
              disabled={view}
              label={t('developers.addEdit.pageTexts.form.phoneNumber.label')}
              placeholder={t('developers.addEdit.pageTexts.form.phoneNumber.placeholder')}
              value={'+91' + (formData.phone || '')}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, 'phone')}
              onChange={(e) => onInputChange(e, 'phone')}
              error={!!validation.fields.find((field) => field.field === 'phone')}
              errorMessage={
                validation.fields.find((field) => field.field === 'phone')?.message ?? ''
              }
            />
          </div>
        </div>

        {/* Website - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.website}
              name={VALIDATION_FIELD_MAPPING.website}
              disabled={view}
              label={t('developers.addEdit.pageTexts.form.website.label')}
              placeholder={t('developers.addEdit.pageTexts.form.website.placeholder')}
              value={formData.website}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.website)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.website)}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.website
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.website
                )?.message ?? ''
              }
            />
          </div>
        </div>

        {/* GST Number - col-md-4 */}
        <div className="col-md-4">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.gst_number}
              name={VALIDATION_FIELD_MAPPING.gst_number}
              disabled={view}
              label={t('developers.addEdit.pageTexts.form.gstNumber.label')}
              placeholder={t('developers.addEdit.pageTexts.form.gstNumber.placeholder')}
              value={formData.gst_number}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.gst_number)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.gst_number)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      {/* Section 2: Address Details */}
      <div className="row g-6">
        <h6 className="mb-0">2. {t('developers.addEdit.pageTexts.form.sections.addressDetails')}</h6>

        {/* Address 1 - col-md-6 */}
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.office_address_1}
              name={VALIDATION_FIELD_MAPPING.office_address_1}
              disabled={view}
              label={t('developers.addEdit.pageTexts.form.address1.label')}
              placeholder={t('developers.addEdit.pageTexts.form.address1.placeholder')}
              value={formData.office_address_1}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.office_address_1)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.office_address_1)}
            />
          </div>
        </div>

        {/* Address 2 - col-md-6 */}
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.office_address_2}
              name={VALIDATION_FIELD_MAPPING.office_address_2}
              disabled={view}
              label={t('developers.addEdit.pageTexts.form.address2.label')}
              placeholder={t('developers.addEdit.pageTexts.form.address2.placeholder')}
              value={formData.office_address_2}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.office_address_2)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.office_address_2)}
            />
          </div>
        </div>

        {/* Country - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={
                formData.countryId && formData.countryId.toString().trim() ? [formData.countryId] : []
              }
              options={(countries || []).map((item: any) => ({
                label: item.name,
                value: item.id,
              }))}
              allOptions={(countries || []).map((item: any) => ({
                label: item.name,
                value: item.id,
              }))}
              required
              id={VALIDATION_FIELD_MAPPING.countryId}
              name={VALIDATION_FIELD_MAPPING.countryId}
              disabled={view}
              searchText={dropdownSearchData.country}
              onSearch={onDropdownSearchChange}
              placeholder={t('developers.addEdit.pageTexts.form.country.placeholder')}
              label={t('developers.addEdit.pageTexts.form.country.label')}
              onChange={onInputChange}
              onBlur={onInputBlur}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.countryId
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.countryId
                )?.message ?? ''
              }
            />
          </div>
        </div>

        {/* State - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={
                formData.stateId && formData.stateId.toString().trim() ? [formData.stateId] : []
              }
              options={(states || []).map((item: any) => ({
                label: item.name,
                value: item.id,
              }))}
              allOptions={(states || []).map((item: any) => ({
                label: item.name,
                value: item.id,
              }))}
              required
              id={VALIDATION_FIELD_MAPPING.stateId}
              name={VALIDATION_FIELD_MAPPING.stateId}
              disabled={view}
              searchText={dropdownSearchData.state}
              onSearch={onDropdownSearchChange}
              placeholder={t('developers.addEdit.pageTexts.form.state.placeholder')}
              label={t('developers.addEdit.pageTexts.form.state.label')}
              onChange={onInputChange}
              onBlur={onInputBlur}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.stateId
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.stateId
                )?.message ?? ''
              }
            />
          </div>
        </div>

        {/* City - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Dropdown
              values={
                formData.cityId && formData.cityId.toString().trim() ? [formData.cityId] : []
              }
              options={(cities || []).map((city: any) => ({
                label: city.name,
                value: city.id,
              }))}
              allOptions={(cities || []).map((city: any) => ({
                label: city.name,
                value: city.id,
              }))}
              required
              id={VALIDATION_FIELD_MAPPING.cityId}
              name={VALIDATION_FIELD_MAPPING.cityId}
              disabled={view}
              searchText={dropdownSearchData.city}
              onSearch={onDropdownSearchChange}
              placeholder={t('developers.addEdit.pageTexts.form.city.placeholder')}
              label={t('developers.addEdit.pageTexts.form.city.label')}
              onChange={onInputChange}
              onBlur={onInputBlur}
              error={
                !!validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.cityId
                )
              }
              errorMessage={
                validation.fields.find(
                  (field) => field.field === VALIDATION_FIELD_MAPPING.cityId
                )?.message ?? ''
              }
            />
          </div>
        </div>

        {/* Pincode - col-md-3 */}
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              id={VALIDATION_FIELD_MAPPING.pincode}
              name={VALIDATION_FIELD_MAPPING.pincode}
              disabled={view}
              label={t('developers.addEdit.pageTexts.form.pincode.label')}
              placeholder={t('developers.addEdit.pageTexts.form.pincode.placeholder')}
              value={formData.pincode}
              onKeyDown={onKeyDown}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.pincode)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.pincode)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      {/* Section 3: Other Information */}
      <div className="row g-6">
        <h6 className="mb-0">3. {t('developers.addEdit.pageTexts.form.sections.otherInformation')}</h6>

        {/* Note - col-md-9 */}
        <div className="col-md-9">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.note}
              name={VALIDATION_FIELD_MAPPING.note}
              disabled={view}
              label={t('developers.addEdit.pageTexts.form.note.label')}
              placeholder={t('developers.addEdit.pageTexts.form.note.placeholder')}
              value={formData.notes}
              onBlur={(e) => onInputBlur(e, VALIDATION_FIELD_MAPPING.note)}
              onChange={(e) => onInputChange(e, VALIDATION_FIELD_MAPPING.note)}
              rows={4}
              className="form-control-lg h-100"
            />
          </div>
        </div>

        {/* Status - col-md-3 */}
        <div className="col-md-3 d-flex align-items-start">
          <div className="form-floating form-floating-outline">
            <Switch
              label={t('developers.addEdit.pageTexts.form.status.label')}
              checked={formData.is_active}
              name={'is_active'}
              disabled={view}
              onChange={(e) => onInputChange(e, 'is_active')}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default DeveloperDetails;
